import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, Select, message, InputNumber, Space } from 'antd';
import Rest from 'tools/rest';
import { MATERIAL_UNITS, placementSchemaForWarehouse } from '../utils';
import CategoryFormModal from './categoryFormModal';

const ADD_CATEGORY_VALUE = '__add_category__';

function buildLocationPayload(values, schema) {
  const loc = {};
  schema.forEach(({ key }) => {
    const v = values[`loc_${key}`];
    if (v !== undefined && v !== null && v !== '') {
      const n = Number(v);
      if (!Number.isNaN(n)) loc[key] = n;
    }
  });
  return Object.keys(loc).length ? loc : {};
}

const MaterialFormModal = ({
  visible,
  material,
  onClose,
  onSuccess,
  variant = 'storage',
}) => {
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [form] = Form.useForm();

  const isEdit = !!material;
  const effectiveVariant = variant === 'registry' ? 'registry' : 'storage';

  const selectedWarehouseId = Form.useWatch('warehouse_id', form);
  const selectedWarehouse = useMemo(
    () => warehouses.find((w) => w.id === selectedWarehouseId),
    [warehouses, selectedWarehouseId]
  );

  const locationSchema = useMemo(
    () => placementSchemaForWarehouse(selectedWarehouse),
    [selectedWarehouse]
  );

  useEffect(() => {
    if (visible) {
      loadOptions();
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    if (variant === 'registry' && material?.id) {
      form.setFieldsValue({
        name: material.name,
        material_category_id: material.material_category_id,
        unit: material.unit || undefined,
      });
      return;
    }
    if (variant === 'registry' && !material) {
      form.resetFields();
      return;
    }

    if (material) {
      const wh = warehouses.find((w) => w.id === material.warehouse_id);
      const schema = placementSchemaForWarehouse(wh);
      const initial = {
        name: material.material_name,
        material_category_id: material.material_category_id,
        warehouse_id: material.warehouse_id,
      };
      if (material.material_unit != null || material.unit != null) {
        initial.unit = material.material_unit || material.unit;
      }
      schema.forEach(({ key }) => {
        const v = material.location && material.location[key];
        initial[`loc_${key}`] = v !== undefined && v !== null ? v : undefined;
      });
      form.setFieldsValue(initial);
    } else {
      form.resetFields();
    }
  }, [visible, material, warehouses, variant, form]);

  const loadOptions = () => {
    Rest.get('/api/v1/material_categories.json').then((r) =>
      setCategories(r.data.material_categories || [])
    );
    Rest.get('/api/v1/warehouses.json').then((r) =>
      setWarehouses(r.data.warehouses || [])
    );
  };

  const handleCategoryCreated = (cat) => {
    setCategories((prev) =>
      [...prev.filter((c) => c.id !== cat.id), cat].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'ru')
      )
    );
    form.setFieldsValue({ material_category_id: cat.id });
  };

  const handleOk = () => {
    const validateFields =
      effectiveVariant === 'registry'
        ? form.validateFields(['name', 'material_category_id', 'unit'])
        : form.validateFields();

    validateFields.then((values) => {
      const materialPayload = {
        name: values.name,
        material_category_id: values.material_category_id,
      };
      if (isEdit) {
        materialPayload.unit = values.unit === undefined || values.unit === null ? null : values.unit;
      } else if (values.unit) {
        materialPayload.unit = values.unit;
      }

      if (effectiveVariant === 'registry' && isEdit && material?.id) {
        setLoading(true);
        Rest.put(`/api/v1/materials/${material.id}.json`, { material: materialPayload })
          .then(() => {
            message.success('Сохранено');
            form.resetFields();
            if (onSuccess) onSuccess();
            onClose();
          })
          .catch((err) => {
            message.error(
              err.response?.data?.errors?.join?.(', ') || 'Ошибка сохранения'
            );
          })
          .finally(() => setLoading(false));
        return;
      }

      if (effectiveVariant === 'registry' && !isEdit) {
        setLoading(true);
        Rest.post('/api/v1/materials.json', { material: materialPayload })
          .then((matResp) => {
            const mat = matResp.data?.material;
            if (!mat) {
              const errs = matResp.data?.errors;
              const msg = errs && typeof errs === 'object'
                ? Object.values(errs).flat().join(', ')
                : 'Ошибка создания';
              message.error(msg);
              return;
            }
            message.success('Позиция добавлена в реестр');
            form.resetFields();
            if (onSuccess) onSuccess(mat);
            onClose();
          })
          .catch((err) => {
            message.error(
              err.response?.data?.errors?.join?.(', ') || 'Ошибка создания'
            );
          })
          .finally(() => setLoading(false));
        return;
      }

      const schema = placementSchemaForWarehouse(
        warehouses.find((w) => w.id === values.warehouse_id)
      );
      const locationJson = buildLocationPayload(values, schema);

      setLoading(true);

      if (isEdit) {
        Promise.all([
          Rest.put(`/api/v1/materials/${material.material_id}.json`, {
            material: materialPayload,
          }),
          Rest.put(`/api/v1/warehouse_materials/${material.id}.json`, {
            warehouse_material: {
              warehouse_id: values.warehouse_id,
              location: locationJson,
            },
          }),
        ])
          .then(() => {
            message.success('Материал обновлён');
            if (onSuccess) onSuccess();
            onClose();
          })
          .catch((err) => {
            message.error(
              err.response?.data?.errors?.join?.(', ') || 'Ошибка обновления'
            );
          })
          .finally(() => setLoading(false));
      } else {
        Rest.post('/api/v1/materials.json', { material: materialPayload })
          .then((matResp) => {
            const materialId = matResp.data.material.id;
            return Rest.post('/api/v1/warehouse_materials.json', {
              warehouse_material: {
                material_id: materialId,
                warehouse_id: values.warehouse_id,
                quantity: 0,
                location: locationJson,
              },
            });
          })
          .then((wmResp) => {
            const wm = wmResp.data?.warehouse_material;
            message.success('Материал создан');
            form.resetFields();
            if (onSuccess) onSuccess(wm);
            onClose();
          })
          .catch((err) => {
            message.error(
              err.response?.data?.errors?.join?.(', ') || 'Ошибка создания'
            );
          })
          .finally(() => setLoading(false));
      }
    });
  };

  return (
    <React.Fragment>
      <Modal
        title={
          effectiveVariant === 'registry' && isEdit
            ? 'Редактирование позиции реестра'
            : isEdit
              ? 'Редактирование материала'
              : (effectiveVariant === 'registry' ? 'Новая позиция в реестре' : 'Новый материал')
        }
        open={visible}
        onOk={handleOk}
        onCancel={onClose}
        confirmLoading={loading}
        okText={isEdit ? 'Сохранить' : 'Создать'}
        cancelText="Отмена"
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: 'Введите название' }]}
          >
            <Input placeholder="Название материала" />
          </Form.Item>

          <Form.Item name="material_category_id" label="Категория">
            <Select
              placeholder="Выберите категорию"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              options={[
                ...categories.map((c) => ({ value: c.id, label: c.name })),
                { value: ADD_CATEGORY_VALUE, label: '+ Добавить категорию' },
              ]}
              onChange={(v) => {
                if (v === ADD_CATEGORY_VALUE) {
                  setCategoryModalVisible(true);
                  form.setFieldsValue({ material_category_id: undefined });
                }
              }}
            />
          </Form.Item>

          <Form.Item name="unit" label="Единица измерения">
            <Select
              allowClear
              placeholder="Необязательно"
              options={MATERIAL_UNITS.map((u) => ({ value: u, label: u }))}
            />
          </Form.Item>

          {effectiveVariant === 'storage' && (
          <Form.Item
            name="warehouse_id"
            label="Склад"
            rules={[{ required: true, message: 'Выберите склад' }]}
          >
            <Select
              placeholder="Выберите склад"
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            />
          </Form.Item>
          )}

          {effectiveVariant === 'storage' && selectedWarehouseId && locationSchema.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8 }}>Размещение</div>
              <Space wrap align="start">
                {locationSchema.map(({ key, label }) => (
                  <Form.Item
                    key={key}
                    name={`loc_${key}`}
                    label={label}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber min={0} placeholder="№" style={{ width: 100 }} />
                  </Form.Item>
                ))}
              </Space>
            </div>
          )}
        </Form>
      </Modal>
      <CategoryFormModal
        visible={categoryModalVisible}
        onClose={() => setCategoryModalVisible(false)}
        onSuccess={handleCategoryCreated}
      />
    </React.Fragment>
  );
};

export default MaterialFormModal;
